import { useReceivedRequestsPaginated } from "../api/queries/useReceivedRequests";
import { useOrderedRequestsPaginated } from "../api/queries/useOrderedRequests";
import { useReceivedContractsPaginated } from "../api/queries/useReceivedContracts";
import { useOrderedContractsPaginated } from "../api/queries/useOrderedContracts";
import { ServiceCategoryEnum } from "../types/service.type";
import { useProfileData } from "../../../hooks/useProfileData";

interface UseServiceRequestsTypeCountsResult {
  receivedCount: number;
  orderedCount: number;
  isLoading: boolean;
}

export const useServiceRequestsTypeCounts = (): UseServiceRequestsTypeCountsResult => {
  const { data: userData, isLoading: isProfileLoading } = useProfileData();
  const { id } = userData || {};

  // Get received requests counts for all categories
  const { data: receivedNewRequests, isLoading: isReceivedNewLoading } = useReceivedRequestsPaginated({
    category: "new" as ServiceCategoryEnum,
    limit: 1,
    enabled: true,
  });

  const { data: receivedHistoryRequests, isLoading: isReceivedHistoryLoading } = useReceivedRequestsPaginated({
    category: "history" as ServiceCategoryEnum,
    limit: 1,
    enabled: true,
  });

  const { data: receivedContracts, isLoading: isReceivedContractsLoading } = useReceivedContractsPaginated({
    category: "contract" as ServiceCategoryEnum,
    limit: 1,
    enabled: !!id,
    userId: String(id),
  });

  // Get ordered requests counts for all categories
  const { data: orderedNewRequests, isLoading: isOrderedNewLoading } = useOrderedRequestsPaginated({
    category: "new" as ServiceCategoryEnum,
    limit: 1,
    enabled: true,
  });

  const { data: orderedHistoryRequests, isLoading: isOrderedHistoryLoading } = useOrderedRequestsPaginated({
    category: "history" as ServiceCategoryEnum,
    limit: 1,
    enabled: true,
  });

  const { data: orderedContracts, isLoading: isOrderedContractsLoading } = useOrderedContractsPaginated({
    category: "contract" as ServiceCategoryEnum,
    limit: 1,
    enabled: !!id,
    userId: String(id),
  });

  const isLoading =
    isProfileLoading ||
    isReceivedNewLoading ||
    isReceivedHistoryLoading ||
    isReceivedContractsLoading ||
    isOrderedNewLoading ||
    isOrderedHistoryLoading ||
    isOrderedContractsLoading;

  // Calculate total counts for each type
  const receivedCount =
    (receivedNewRequests?.meta.totalItems ?? 0) +
    (receivedHistoryRequests?.meta.totalItems ?? 0) +
    (receivedContracts?.data.meta.totalItems ?? 0);

  const orderedCount =
    (orderedNewRequests?.data.meta.totalItems ?? 0) +
    (orderedHistoryRequests?.data.meta.totalItems ?? 0) +
    (orderedContracts?.data.meta.totalItems ?? 0);

  return {
    receivedCount,
    orderedCount,
    isLoading,
  };
};
